import React from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import Cookies from 'js-cookie';
import DealFlowViewContext from 'context/DealFlowViewContext';
import ApplicationsListing from 'views/startup_applications';
import DealFlowBoard from 'views/deal_flow_board';

// Remember the last List/Cards choice so leaving Deal Flow (e.g. to Reels) and
// coming back restores it, instead of resetting to 'list' on every mount.
const VIEW_COOKIE = 'deal_flow_view';

/**
 * Deal Flow route shell. Renders EITHER the table ('list') or the kanban board
 * ('cards') on the same route — the List/Cards toggle only flips this state, so
 * the URL never changes. Each view keeps its own data fetching and state.
 */
const DealFlowShell = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // Board is the default: with no stored choice (first visit / first login) Deal Flow
    // opens on the board, and only an explicit switch to the table is remembered. The
    // test is inverted for exactly that reason - "no cookie" must mean cards, not list.
    const [view, setViewState] = React.useState(() => (Cookies.get(VIEW_COOKIE) === 'list' ? 'list' : 'cards'));
    // Persist every toggle so the choice survives navigation.
    const setView = React.useCallback((next) => {
        setViewState(next);
        Cookies.set(VIEW_COOKIE, next);
    }, []);
    const value = React.useMemo(() => ({ view, setView }), [view, setView]);

    // Board (cards) is desktop-only; phones are list-only. Force the list render below
    // md regardless of the stored view, so a board user who shrinks to mobile isn't
    // stranded (the toggle is hidden there too). Desktop keeps `view` unchanged, so the
    // board selection is remembered when they go back to a wide screen.
    const effectiveView = isMobile ? 'list' : view;

    return (
        <DealFlowViewContext.Provider value={value}>
            {effectiveView === 'cards' ? <DealFlowBoard /> : <ApplicationsListing />}
        </DealFlowViewContext.Provider>
    );
};

export default DealFlowShell;
